import * as React from 'react';

import styled, { css } from 'styled-components';

import StyledInput from './styled-input';
import StyledLabel from './styled-label';
import StyledError from './styled-error';

import { InputDecorationType } from './input-decoration';

import DeleteIcon from '@components/basics/icons/delete';
import { AnimatePresence, motion } from 'framer-motion';
import { hasEmoji, removeEmoji } from '@webapp/utils/validators';

export interface InputProps {
  children?: React.ReactChild;
  className?: string;
  classNameInput?: string;
  locked?: boolean;
  focussed?: boolean;
  active?: boolean;
  value?: any;
  error?: React.ReactNode;
  label?: React.ReactNode;
  duration?: string;
  autoComplete?: string;
  placeholder?: string;
  id?: string;
  type?: string;
  name?: string;
  rightDecoration?: any;
  leftDecoration?: any;
  bigDecoration?: boolean;
  biggerDecoration?: boolean;
  hasTruncate?: boolean;
  tabIndex?: number;
  maxLength?: number;
  customBorderRadius?: string;
  propagateClick?: boolean;
  hasClose?: boolean;
  isGrey?: boolean;
  isDark?: boolean;
  hideLabel?: boolean;
  style?: React.CSSProperties;
  alignRight?: boolean;
  small?: boolean;
  yellowValue?: boolean;
  yellowLine?: boolean;
  redLine?: boolean;
  redValue?: boolean;
  onClose?: (ev: React.MouseEvent<SVGSVGElement, MouseEvent>) => void;
  onChange?: (value: string, ev?: React.ChangeEvent<HTMLInputElement>) => void;
  onKeyDown?: (ev?: React.KeyboardEvent<HTMLInputElement>) => void;
  onFocus?: () => void;
  onBlur?: () => void;
  onClick?: () => void;
  pattern?: string;
  min?: number;
  max?: number;
  step?: number;
}

interface InputState {
  active: boolean;
  autoFill?: boolean;
}

export class InputField extends React.Component<InputProps> {
  state: InputState;

  constructor(props: InputProps) {
    super(props);

    this.state = {
      active: (props.locked && props.active) || !!props.value,
    };
  }

  changeValue(event: React.ChangeEvent<HTMLInputElement>) {
    let value = event.target.value;
    if (hasEmoji(value)) {
      value = removeEmoji(value);
    }

    this.setState({
      active: true,
    });
    if (this.props.onChange) {
      this.props.onChange(value, event);
    }
  }
  changeOnKeyDownValue(
    event: React.KeyboardEvent<HTMLInputElement> &
      React.ChangeEvent<HTMLInputElement>
  ) {
    this.setState({
      active: true,
    });
    if (this.props.onKeyDown) this.props.onKeyDown(event);
  }

  handleFocus() {
    if (!this.props.locked) {
      this.setState({ active: true });
      if (this.props.onFocus) {
        this.props.onFocus();
      }
    }
  }

  handleBlur() {
    if (!this.props.locked) {
      if (!this.props.value) {
        this.setState({ active: false });
      }
      if (this.props.onBlur) {
        this.props.onBlur();
      }
    }
  }

  // This is for the
  handleAutoFill = (e) => {
    this.setState({
      active: e.animationName === 'onAutoFillStart',
    });
  };

  render() {
    const { active } = this.state;
    const decorationType: InputDecorationType | undefined =
      this.props.rightDecoration && this.props.leftDecoration
        ? 'both'
        : this.props.rightDecoration
        ? 'right'
        : this.props.leftDecoration
        ? 'left'
        : undefined;

    return (
      <div
        className={`${this.props.className ? this.props.className : ''} ${
          active ? 'active' : ''
        } ${this.props.locked ? 'locked' : ''} input-wrapper`}
        style={this.props.style}
        onClick={this.props.onClick ? this.props.onClick : undefined}
      >
        <StyledInput
          id={this.props.id}
          type={this.props.type}
          classNameInput={`${
            this.props.classNameInput ? this.props.classNameInput : ''
          } ${this.props.isGrey ? 'grey' : ''}`}
          value={this.props.value}
          placeholder={this.props.placeholder}
          active={active || !!this.props.value || !!this.props.placeholder}
          withDecoration={decorationType}
          bigDecoration={this.props.bigDecoration}
          biggerDecoration={this.props.biggerDecoration}
          autoComplete={this.props.autoComplete}
          tabIndex={this.props.tabIndex}
          hasTruncate={this.props.hasTruncate}
          maxLength={this.props.maxLength}
          noLabel={this.props.hideLabel}
          onChange={(ev: React.ChangeEvent<HTMLInputElement>) =>
            this.props.locked ? null : this.changeValue(ev)
          }
          onKeyDown={(
            ev: React.KeyboardEvent<HTMLInputElement> &
              React.ChangeEvent<HTMLInputElement>
          ) => (this.props.locked ? null : this.changeOnKeyDownValue(ev))}
          onFocus={() => this.handleFocus()}
          onBlur={() => this.handleBlur()}
          isDark={this.props.isDark}
          disabled={this.props.locked}
          propagateClick={this.props.propagateClick}
          min={this.props.min}
          max={this.props.max}
          step={this.props.step}
          alignRight={this.props.alignRight}
          small={this.props.small}
          yellowValue={this.props.yellowValue}
          redValue={this.props.redValue}
          hasError={!!this.props.error && !this.props.locked}
          onAnimationStart={(e) => this.handleAutoFill(e)}
        />
        {this.props.leftDecoration ? (
          <div className="decoration-left">{this.props.leftDecoration}</div>
        ) : null}
        {this.props.rightDecoration ? (
          <div className="decoration-right">{this.props.rightDecoration}</div>
        ) : null}
        <AnimatePresence>
          {this.props.error && !this.props.locked ? (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <StyledError
                className={
                  this.props.rightDecoration ? 'error-right-decoration' : ''
                }
              >
                {this.props.error}
              </StyledError>
            </motion.div>
          ) : null}
        </AnimatePresence>
        {this.props.label ? (
          <StyledLabel
            label={this.props.label}
            id={this.props.id}
            active={active || !!this.props.value || !!this.props.placeholder}
            withDecoration={decorationType}
            bigDecoration={this.props.bigDecoration}
            biggerDecoration={this.props.biggerDecoration}
            alignRight={this.props.alignRight}
            small={this.props.small}
            isDark={this.props.isDark}
          />
        ) : null}
        {this.props.hasClose ? (
          <DeleteIcon className="delete-icon" onClick={this.props.onClose} />
        ) : null}
      </div>
    );
  }
}

export default styled(InputField)`
  width: auto;
  min-height: 48px;
  border-radius: ${(props) =>
    props.customBorderRadius ? props.customBorderRadius : '4px'};

  border: 1px solid transparent;
  position: relative;
  background-color: ${({ theme }) => theme.colors.inputBackground};
  transition: border
    ${({ theme }) => `${theme.transitions.duration} ${theme.transitions.type}`};
  border-left-color: ${(props) =>
    (props.error && !props.locked) || props.redLine
      ? css`
          ${({ theme }) => theme.colors.error};
        `
      : props.isGrey
      ? css`
          ${({ theme }) => theme.colors.inputBackgroundAlternate};
        `
      : props.isDark
      ? css`
          ${({ theme }) => theme.colors.black};
        `
      : props.yellowLine
      ? css`
          ${({ theme }) => theme.colors.upload};
        `
      : css`
          ${({ theme }) => theme.colors.inputBackground};
        `};

  ${(props) =>
    (props.error && !props.locked) || props.yellowLine || props.redLine
      ? css`
          border-left-width: 3px;
        `
      : null}

  ${({ theme }) => `@media screen and ${theme.device.tablet}`} {
    min-height: 60px;
  }

  ${(props) =>
    props.isGrey
      ? css`
          background-color: ${({ theme }) =>
            theme.colors.inputBackgroundAlternate};
        `
      : null}
  ${(props) =>
    props.isDark
      ? css`
          background-color: ${({ theme }) => theme.colors.black};
        `
      : null}

  ${(props) =>
    props.small
      ? css`
          min-height: 50px;
          @media ${({ theme }) => theme.device.tablet} {
            min-height: 50px;
          }
        `
      : null}
  ${(props) =>
    !props.locked || !props.error
      ? css`
          &:not(.locked) {
            &:hover,
            &:active,
            &:focus,
            &:focus-within {
              border-color: ${({ theme }) => theme.colors.secondary};
            }
          }
        `
      : null}

  .decoration-right {
    display: flex;
    align-items: center;
    position: absolute;
    top: 50%;
    right: 12px;
    transform: translate(0, -50%);
    z-index: 3;

    ${({ theme }) => `@media screen and ${theme.device.tablet}`} {
      right: 18px;
    }
  }

  .error-right-decoration {
    right: 60px;
  }

  .decoration-left {
    position: absolute;
    top: 50%;
    right: auto;
    left: ${(props) =>
      props.bigDecoration ? '24px' : props.biggerDecoration ? '64px' : '17px'};
    transform: translate(-50%, -50%);
    display: flex;
    align-items: center;

    ${({ theme }) => `@media screen and ${theme.device.tablet}`} {
      left: ${(props) =>
        props.bigDecoration
          ? '32px'
          : props.biggerDecoration
          ? '72px'
          : '22px'};
    }
  }

  ${(props) =>
    props.locked
      ? css`
          cursor: not-allowed;

          input,
          label {
            opacity: 0.6;
          }

          ${props.hideLabel &&
          css`
            input,
            label {
              opacity: 1;
            }
          `}
        `
      : null}

  ${(props) =>
    props.hasClose
      ? css`
          .delete-icon {
            position: absolute;
            top: -12px;
            left: -12px;
            z-index: 4;

            ${({ theme }) => `@media screen and ${theme.device.tablet}`} {
              opacity: 0;
              pointer-events: none;
              transition: opacity
                ${({ theme }) =>
                  `${theme.transitions.duration} ${theme.transitions.type}`};
            }
          }

          @media ${({ theme }) => theme.device.tablet} {
            &:hover {
              .delete-icon {
                opacity: 1;
                pointer-events: all;
              }
            }
          }
        `
      : null}
`;
